import Button from '@mui/material/Button'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import { values } from 'ramda'
import React from 'react'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom-v5-compat'

import ErrorLayout from '@/app/component/layout/error'
import IconText from '@/app/component/layout/icon-text'
import { TYPES } from '@/app/module/campaigns/definitions'

type GuardItem = {
  id?: number
  type?: string
}

type Props = {
  item: GuardItem // API payload; starts empty ({}) until the fetch resolves
  itemId?: number // ID from the URL; present for edit flows even before item loads
  loading?: boolean
  type?: string // type from the URL; takes precedence over item.type (see below)
}

type ComponentEntry = React.ComponentType<any>

type ComponentMap = Record<string, ComponentEntry>

// Factory that wraps a map of campaign-type components with guard logic.
// Call it once per step (e.g. settings, content) to get a guarded component.
export default function createCampaignGuard(componentMap: ComponentMap) {
  const CampaignGuard: React.FC<Props> = (props) => {
    const navigate = useNavigate()
    const { item, itemId, loading, type } = props
    const itemType = type || item.type
    const Content = itemType ? componentMap[itemType] : undefined
    const isLoading = loading || (itemId && !item.id)

    if (!Content && isLoading) {
      return <div data-testid="campaign-guard-loading" style={{ height: '150px' }} />
    }

    if (!Content) {
      return (
        <ErrorLayout status={itemType ? 404 : undefined} style={{ maxWidth: '100%' }}>
          <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
            {itemType && (
              <div>
                <Typography gutterBottom={true}>{`The type of ${itemType} campaign does not exist.`}</Typography>
                <Button color="primary" variant="contained" component={Link} to="/campaigns/create">
                  Start over
                </Button>
              </div>
            )}
            {!itemType && (
              <div>
                <Typography gutterBottom={true}>
                  Before proceeding you need to choose a campaign type from the following available ones:
                </Typography>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {values(TYPES).map(({ name, title, icon, url }) => {
                    const IconComp = icon as React.ComponentType
                    return (
                      <Button
                        key={name}
                        color="primary"
                        variant="outlined"
                        style={{ margin: '2rem 1rem' }}
                        onClick={() => {
                          navigate(`/campaigns/create/${url}`)
                        }}
                      >
                        <IconText>
                          {typeof icon === 'string' ? <Icon style={{ fontSize: '3rem' }}>{icon}</Icon> : <IconComp />}
                          <br />
                          <Typography>{title}</Typography>
                        </IconText>
                      </Button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </ErrorLayout>
      )
    }

    return <Content {...props} />
  }
  return CampaignGuard
}

export type { Props as CampaignGuardProps }
